import { isImageFile } from '@/services/assistant/attachments/classify-attachment'
import { useCallback, useRef, useState, type ClipboardEvent, type DragEvent } from 'react'

function carriesFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files')
}

/**
 * Dropping files onto the assistant panel, and pasting pictures into its input. A drag of text or a
 * link is left alone; only files are taken. `dragging` is true while files hover over the panel.
 */
export function useAttachmentDrop(addFiles: (files: File[]) => void, disabled: boolean) {
  const [dragging, setDragging] = useState(false)
  // dragenter/dragleave fire for every child crossed, so a depth count tells "left the panel" apart.
  const depth = useRef(0)

  const onDragEnter = useCallback(
    (e: DragEvent) => {
      if (!carriesFiles(e) || disabled) return
      e.preventDefault()
      depth.current += 1
      setDragging(true)
    },
    [disabled],
  )

  const onDragOver = useCallback(
    (e: DragEvent) => {
      if (!carriesFiles(e) || disabled) return
      // Without this the browser refuses the drop and opens the file instead.
      e.preventDefault()
    },
    [disabled],
  )

  const onDragLeave = useCallback((e: DragEvent) => {
    if (!carriesFiles(e)) return
    depth.current = Math.max(0, depth.current - 1)
    if (depth.current === 0) setDragging(false)
  }, [])

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (!carriesFiles(e)) return
      e.preventDefault()
      depth.current = 0
      setDragging(false)
      if (disabled) return
      const files = Array.from(e.dataTransfer.files ?? [])
      if (files.length) addFiles(files)
    },
    [addFiles, disabled],
  )

  const onPaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      const images = Array.from(e.clipboardData?.files ?? []).filter(isImageFile)
      // Text pastes as text; only a pasted picture becomes an attachment.
      if (images.length === 0 || disabled) return
      e.preventDefault()
      addFiles(images)
    },
    [addFiles, disabled],
  )

  return { dragging, dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop }, onPaste }
}
